import { adminUserRepository } from '../repositories/adminUserRepository';
import type {
  AdminUserActivity,
  AdminUserJobActivity,
  AdminUserProjectActivity,
  AdminUserQuoteActivity,
} from '../types/admin_user_activity';

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readString = (record: UnknownRecord, key: string): string => {
  const value = record[key];
  if (typeof value !== 'string') {
    throw new Error('Invalid admin user activity response');
  }
  return value;
};

const readNullableString = (record: UnknownRecord, key: string): string | null => {
  const value = record[key];
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string') {
    throw new Error('Invalid admin user activity response');
  }
  return value;
};

const parseQuote = (value: unknown): AdminUserQuoteActivity => {
  if (!isRecord(value)) throw new Error('Invalid admin user activity response');
  return {
    id: readString(value, 'id'),
    job_id: readString(value, 'job_id'),
    admin_quote_id: readString(value, 'admin_quote_id'),
    principal_quote_id: readString(value, 'principal_quote_id'),
    job_title: readString(value, 'job_title'),
    quote_status: readString(value, 'quote_status'),
    job_status: readString(value, 'job_status'),
    created_at: readString(value, 'created_at'),
    submitted_at: readNullableString(value, 'submitted_at'),
  };
};

const parseProject = (value: unknown): AdminUserProjectActivity => {
  if (!isRecord(value)) throw new Error('Invalid admin user activity response');
  return {
    id: readString(value, 'id'),
    job_id: readString(value, 'job_id'),
    admin_quote_id: readString(value, 'admin_quote_id'),
    principal_quote_id: readString(value, 'principal_quote_id'),
    job_title: readString(value, 'job_title'),
    job_status: readString(value, 'job_status'),
    quote_status: readString(value, 'quote_status'),
    created_at: readString(value, 'created_at'),
    completed_at: readNullableString(value, 'completed_at'),
  };
};

const parseJob = (value: unknown): AdminUserJobActivity => {
  if (!isRecord(value)) throw new Error('Invalid admin user activity response');
  const quotesCount = value.quotes_count;
  if (typeof quotesCount !== 'number') {
    throw new Error('Invalid admin user activity response');
  }
  return {
    id: readString(value, 'id'),
    job_id: readString(value, 'job_id'),
    title: readString(value, 'title'),
    status: readString(value, 'status'),
    visibility: readString(value, 'visibility'),
    created_at: readString(value, 'created_at'),
    published_at: readNullableString(value, 'published_at'),
    quotes_count: quotesCount,
  };
};

const parseCollection = <T>(value: unknown, parser: (item: unknown) => T): T[] => {
  if (value === null || value === undefined) return [];
  if (!Array.isArray(value)) throw new Error('Invalid admin user activity response');
  return value.map(parser);
};

export const adminUserService = {
  async fetchActivity(profileId: string): Promise<AdminUserActivity> {
    const response = await adminUserRepository.fetchActivity(profileId);
    if (!isRecord(response)) {
      throw new Error('Invalid admin user activity response');
    }

    return {
      quotes: parseCollection(response.quotes, parseQuote),
      projects: parseCollection(response.projects, parseProject),
      jobs: parseCollection(response.jobs, parseJob),
    };
  },

  async fetchCreditBalance(profileId: string): Promise<number> {
    const response = await adminUserRepository.fetchCreditBalance(profileId);
    if (typeof response !== 'number') {
      throw new Error('Invalid admin credit balance response');
    }
    return response;
  },
};
